import { Injectable } from '@angular/core';
import { WellnessOption } from '@constants/wellness';
import { Query } from '@datorama/akita';
import { Contact } from '@models/Contact';
import { InsuranceType } from '@models/InsuranceType';
import { Modality } from '@models/Modality';
import { PaymentSchedule } from '@models/PaymentSchedule';
import { Pet } from '@models/Pet';
import { Quote } from '@models/Quote';
import { QuoteSession } from '@models/QuoteSession';
import { Species } from '@models/Species';
import { UtmParameters } from '@models/UtmParameters';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { QuoteSessionStore } from './session.store';

@Injectable({ providedIn: 'root' })
export class QuoteSessionQuery extends Query<QuoteSession> {
	constructor(protected store: QuoteSessionStore) {
		super(store);
	}

	/**
	 * Returns the session value (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getSession = (): QuoteSession => this.getValue();
	/** Returns the full session object as an Observable */
	public readonly session$ = (): Observable<QuoteSession> => this.select(state => state);
	/** Returns the session type of the quote */
	public readonly sessionType$ = (): Observable<string> => this.select(state => state.sessionType);
	/** Returns the current brand of the quote */
	public readonly brand$ = (): Observable<string> => this.select(state => state?.contact?.brand ?? '');
	/**
	 * Returns the brand value (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getBrandValue = (): string | undefined => this.getValue().contact.brand;
	/** Returns the org code as an Observable */
	public readonly organizationCode$ = (): Observable<string | undefined> => this.select(state => state.contact.organizationCode);
	/**
	 * Returns the org code value (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getOrganizationCodeValue = (): string | undefined => this.getValue().contact.organizationCode;
	/** Returns the analytics info as an Observable */
	public readonly analyticsInfo$ = (): Observable<UtmParameters> => this.select(state => state.analyticsInfo);
	/**
	 * Returns the delay start date value (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getDelayStartDate = (): number => this.getValue().delayPolicyStartDateDays;
	/**
	 * Gets total policy premium value (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getQuoteTotalPolicyPremium = (): number | undefined => this.getValue().quoteDetails.totalPolicyPremium;
	/**
	 * Gets the `policyNumber` value (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getPolicyNumber = (): string | undefined => this.getValue().quoteDetails.policyNumber;
	/**
	 * Gets the `quoteId` value (non-observable)
	 *
	 */
	public readonly getQuoteId = (): string => this.getValue().id;
	/**
	 * Gets the US state used for the quote (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getQuoteState = (): string => this.getValue().quoteState;
	/**
	 * Returns the `pet` given the ID (non-observable)
	 *
	 * @param petId The GUID that identifies the pet
	 *
	 * @deprecated
	 */
	public readonly getPetById = (petId: string): Pet | undefined => this.getValue().pets?.find(pet => pet.petId === petId);
	/** Gets the `pet` given the ID as an Observable */
	// eslint-disable-next-line max-len
	public readonly getPetById$ = (petId: string): Observable<Pet | undefined> => this.select('pets').pipe(map(pets => pets.find(pet => pet.petId === petId)));
	public readonly getActivePetSupplementalType = (petId: string): string | undefined => this.getPetById(petId)?.supplementalType;
	/**
	 * Gets the `contact` values (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getContact = (): Contact => this.getValue().contact;
	/** Gets the `ratingState` as an Observable */
	public readonly ratingState$ = (): Observable<string | undefined> => this.select(state => state.contact.ratingState);
	/** Gets the `vetSearchZip` code as an Observable */
	public readonly vetSearchZip$ = (): Observable<string | undefined> => this.select(state => state?.contact?.vetSearchZip);
	/** Gets the pet parent's answer to "How did you hear about us?" as an Observable */
	public readonly heardAboutUs$ = (): Observable<number | undefined> => this.select(state => state?.contact?.heardAboutUs);
	/** Gets the `quoteDetails` as an Observable */
	public readonly quoteDetailsState$ = (): Observable<Quote> => this.select(state => state.quoteDetails);
	/**
	 * Returns current pet's `pet index` (non-observable)
	 */
	public readonly getPetIndex = (petId: string): number => this.getValue().pets.findIndex(pet => pet.petId === petId);
	/**
	 * Returns `quoteDetails` (non-observable)
	 *
	 */
	public readonly getQuoteDetails = (): Quote => this.getValue().quoteDetails;
	/** Returns `contact` as an Observable */
	public readonly contactState$ = (): Observable<Contact> => this.select('contact');
	/**
	 * Returns `contactId` (non-observable)
	 *
	 * @deprecated
	 */
	public readonly contactIdValue = (): string | undefined => this.getValue().contact.id;
	/** Returns `contactEmail` as an Observable */
	public readonly contactEmail$ = (): Observable<string | undefined> => this.select(state => state.contact.emailAddress);
	/** Returns `military` as an Observable */
	public readonly contactMilitary$ = (): Observable<boolean | undefined> => this.select(state => state.contact.military);
	/** Returns `pets` as an Observable */
	public readonly petsState$ = (): Observable<Pet[]> => this.select('pets');
	/**
	 * Returns `pets` (non-observable)
	 */
	public readonly petsStateValue = (): Pet[] => this.getValue().pets;
	/** Returns `sessionId` as an Observable */
	public readonly sessionId$ = (): Observable<string> => this.select(state => state.id);
	/** Returns `paymentFrequency` as an Observable */
	public readonly paymentFrequency$ = (): Observable<Modality | undefined> => this.select(state => state.quoteDetails.paymentModality);
	/** Returns `paymentSchedule` as an Observable */
	public readonly paymentSchedule$ = (): Observable<PaymentSchedule[]> => this.select(state => state.paymentSchedules);
	/** Returns the policy start date */
	public readonly policyStartDate$ = (): Observable<Date | undefined> => this.select(state => state.quoteDetails.policyStartDate);
	/** Gets the date that the first payment is due on */
	public readonly firstPaymentDue$ = (): Observable<Date | string> => this.select(state => state.paymentSchedules)
		.pipe(
			map(d => d[0].billDate),
		);
	/**
	 * Returns `vetVisit` (non-observable)
	 *
	 * @deprecated
	 */
	public readonly getVetVisit = (): boolean | 'yes' | 'no' | undefined => this.getValue().quoteDetails.hadVetVisit;
	/** Returns true if all pets have visited vet */
	public readonly hadVetVisit$ = (): Observable<boolean | undefined | `yes` | `no`> =>
		this.select(state => state.quoteDetails.hadVetVisit);
	/** Whether or not wellness should be available on the quote as an (Observable) */
	public readonly policyHasIllness$ = (): Observable<boolean> => this.select(state => state.pets)
		.pipe(map(pets => pets.some(pet => pet.insuranceType === InsuranceType.AccidentIllness)));
	/** Whether or not orthopedic waiting periods apply for the quote as an (Observable) */
	public readonly policyHasOrtho$ = (): Observable<boolean> => this.select(state => state.pets)
		.pipe(
			map(pets => (
				pets
					.filter(pet => pet.species === Species.Dog)
					.some(pet => pet.insuranceType === InsuranceType.AccidentIllness)
			)),
		);
	/** Returns `figoState` as an Observable */
	public readonly figoState$ = (): Observable<string> => this.select(state => state.figoState);
	public readonly wellnessOptions$ = (): Observable<WellnessOption[]> => this.select(state => state.wellnessOptions);
}
